import { create } from 'zustand'

import { getEnvName } from '~/config'
import type { Me, WorldSnapshot } from '~/data/types'

interface AppState {
  me: Me | null
  selectedOrgId: string | null
  snapshot: WorldSnapshot | null
  /** userId of the character whose detail card is open, if any. */
  focusedUserId: string | null
  /** pulseId of the room whose panel is open, if any (exclusive with a person). */
  focusedRoomId: string | null
  /** pulseId of the room a "+ hire" desk was clicked in, if any. */
  hireRoomId: string | null

  setMe: (me: Me | null) => void
  selectOrg: (orgId: string | null) => void
  setSnapshot: (snapshot: WorldSnapshot | null) => void
  focusUser: (userId: string | null) => void
  focusRoom: (pulseId: string | null) => void
  openHire: (pulseId: string | null) => void
  reset: () => void
}

// Scope the remembered org by environment: staging and prod have separate org
// id namespaces, so a staging selection must not bleed into a prod session.
const STORAGE_KEY = `glass-office.orgId.${getEnvName()}`
const storedOrgId =
  typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null

export const useAppStore = create<AppState>((set) => ({
  me: null,
  selectedOrgId: storedOrgId,
  snapshot: null,
  focusedUserId: null,
  focusedRoomId: null,
  hireRoomId: null,

  setMe: (me) => set({ me }),
  // Remember the last org so a reload drops straight back into it.
  selectOrg: (selectedOrgId) => {
    if (typeof localStorage !== 'undefined') {
      if (selectedOrgId) localStorage.setItem(STORAGE_KEY, selectedOrgId)
      else localStorage.removeItem(STORAGE_KEY)
    }
    set({ selectedOrgId, snapshot: null })
  },
  setSnapshot: (snapshot) => set({ snapshot }),
  // One panel at a time: focusing a person closes the room panel and vice versa.
  focusUser: (focusedUserId) =>
    set(
      focusedUserId
        ? { focusedUserId, focusedRoomId: null, hireRoomId: null }
        : { focusedUserId },
    ),
  focusRoom: (focusedRoomId) =>
    set(
      focusedRoomId
        ? { focusedRoomId, focusedUserId: null, hireRoomId: null }
        : { focusedRoomId },
    ),
  openHire: (hireRoomId) =>
    set(
      hireRoomId
        ? { hireRoomId, focusedUserId: null, focusedRoomId: null }
        : { hireRoomId },
    ),
  reset: () => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY)
    set({
      selectedOrgId: null,
      snapshot: null,
      focusedUserId: null,
      focusedRoomId: null,
      hireRoomId: null,
    })
  },
}))
